import { useState, useEffect } from "react";
import { useAuth } from 'wasp/client/auth';
import { useQuery, useAction } from 'wasp/client/operations';
import { getUserProfile, updateUserProfile } from 'wasp/client/operations';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Separator } from "../ui/separator";
import { SaveBar } from "./SaveBar";
import { DietarySection } from "./sections/DietarySection";
import { GoalsMacrosSection } from "./sections/GoalsMacrosSection";
import { HouseholdEquipmentSection } from "./sections/HouseholdEquipmentSection";
import { PreferencesPantrySection } from "./sections/PreferencesPantrySection";
import { NotificationsEmailSection } from "./sections/NotificationsEmailSection";
import { ChefLexMessage } from "../Errors/ChefLexMessage";
import { ProfileToast } from "./ProfileToast";
import { ProfileCompleteness } from "./ProfileCompleteness";
import { User, Zap, RotateCcw, Mail } from "lucide-react";
import { HouseholdSizeUpgradePrompt } from "../../../components/HouseholdSizeUpgradePrompt";
import type { V32UITier } from '../../../types/subscription';

type SubscriptionTier = V32UITier;

// Data adapter functions to transform between backend and UI formats
export function transformBackendToUI(backendProfile: any | null | undefined): UserProfile {
  if (!backendProfile) {
    // Return default profile structure
    return {
      diet: "",
      allergies: [],
      avoids: [],
      likes: [],
      dislikes: [],
      timePreference: 30,
      budgetLevel: 50,
      goal: "",
      macros: { calories: 2000, protein: 150, carbs: 200, fat: 65 },
      proteinPriority: false,
      householdSize: 1,
      equipment: [],
      batchPrep: "none",
      familyScaling: 1,
      pantry: [],
      favorites: [],
      neverAgain: [],
      heatLevel: 2,
      reheating: [],
      emailVerified: false,
      weeklyEmail: true,
      emailDay: "sunday",
      emailTime: "18:00",
      pdfFormat: "compact",
      budgetTips: false
    };
  }

  return {
    // Map backend fields to UI format
    diet: backendProfile?.dietaryPreferences?.[0] ?? "",
    allergies: backendProfile?.dietaryPreferences?.filter((d: string) => d.includes('allergy')) ?? [],
    avoids: backendProfile?.dietaryPreferences?.filter((d: string) => d.includes('avoid')) ?? [],
    likes: backendProfile?.flavorPreferences ?? [],
    dislikes: [],
    timePreference: backendProfile?.prepTime === '15-30min' ? 20 : backendProfile?.prepTime === '30-45min' ? 35 : 60,
    budgetLevel: backendProfile?.budgetRange === 'low' ? 25 : backendProfile?.budgetRange === 'high' ? 75 : 50,
    goal: backendProfile?.goals?.[0] ?? "",
    macros: { calories: 2000, protein: 150, carbs: 200, fat: 65 }, // Default since not stored in backend
    proteinPriority: false,
    householdSize: backendProfile?.householdSize ?? 1,
    equipment: backendProfile?.kitchenEquipment ?? [],
    batchPrep: "none",
    familyScaling: backendProfile?.householdSize ?? 1,
    pantry: [],
    favorites: [],
    neverAgain: [],
    heatLevel: backendProfile?.spiceTolerance === 'mild' ? 1 : backendProfile?.spiceTolerance === 'hot' ? 3 : 2,
    reheating: [],
    emailVerified: true, // display only
    weeklyEmail: backendProfile?.weeklyDigestOptIn !== false,
    emailDay: "sunday",
    emailTime: "18:00",
    pdfFormat: "compact",
    budgetTips: false
  };
}

export function transformUIToBackend(uiProfile: UserProfile): any {
  return {
    dietaryPreferences: [
      uiProfile.diet ?? '',
      ...(uiProfile.allergies ?? []),
      ...(uiProfile.avoids ?? [])
    ].filter(Boolean),
    cookingConfidence: 3, // Default
    budgetRange: (uiProfile.budgetLevel ?? 50) < 40 ? 'low' : (uiProfile.budgetLevel ?? 50) > 60 ? 'high' : 'moderate',
    householdSize: uiProfile.householdSize ?? 1,
    mealTypes: ['dinner'], // Default
    prepTime: (uiProfile.timePreference ?? 30) <= 25 ? '15-30min' : (uiProfile.timePreference ?? 30) <= 40 ? '30-45min' : '45-60min',
    kitchenEquipment: uiProfile.equipment ?? [],
    flavorPreferences: uiProfile.likes ?? [],
    spiceTolerance: (uiProfile.heatLevel ?? 2) <= 1 ? 'mild' : (uiProfile.heatLevel ?? 2) >= 3 ? 'hot' : 'medium',
    goals: uiProfile.goal ? [uiProfile.goal] : [],
    weeklyDigestOptIn: Boolean(uiProfile.weeklyEmail ?? true)
  };
}

interface UserProfile {
  // Dietary & Restrictions
  diet: string;
  allergies: string[];
  avoids: string[];
  likes: string[];
  dislikes: string[];
  timePreference: number;
  budgetLevel: number;
  
  // Goals & Macros (Home Chef+)
  goal: string;
  macros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  proteinPriority: boolean;
  
  // Household & Equipment (Master Chef+)
  householdSize: number;
  equipment: string[];
  batchPrep: string;
  familyScaling: number;
  
  // Preferences & Pantry
  pantry: string[];
  favorites: string[];
  neverAgain: string[];
  heatLevel: number;
  reheating: string[];
  
  // Notifications & Email
  emailVerified: boolean;
  weeklyEmail: boolean;
  emailDay: string;
  emailTime: string;
  pdfFormat: string;
  budgetTips: boolean;
}

interface KitchenProfilePageProps {
  userName: string;
  userEmail: string;
  currentTier: SubscriptionTier;
  onClose: () => void;
  onUpgrade?: () => void;
}

export function KitchenProfilePage({
  userName,
  userEmail,
  currentTier,
  onClose,
  onUpgrade
}: KitchenProfilePageProps) {
  // Load user profile data from backend
  const { data: user } = useAuth();
  const isAuthenticated = !!user;
  const { data: backendProfile, isLoading: profileLoading, error: profileError } = useQuery(getUserProfile, {}, { enabled: isAuthenticated });
  const updateUserProfileAction = useAction(updateUserProfile);
  
  const [profile, setProfile] = useState<UserProfile>(transformBackendToUI(null));

  const [originalProfile, setOriginalProfile] = useState<UserProfile>(transformBackendToUI(null));
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("dietary");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "info",
    isVisible: false
  });

  const getTierDisplayName = (tier: SubscriptionTier) => {
    switch (tier) {
      case "starter": return "Duo";
      case "core_plus": return "Core Plus";
      default: return "Demo";
    }
  };

  const getTierColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case "starter": return "bg-supporting text-white";
      case "core_plus": return "bg-coral text-white";
      default: return "bg-supporting text-white";
    }
  };

  // Initialize profile data when backend data loads
  useEffect(() => {
    if (backendProfile && !isInitialized) {
      const transformedProfile = transformBackendToUI(backendProfile);
      setProfile(transformedProfile);
      setOriginalProfile(transformedProfile);
      setIsInitialized(true);
    }
  }, [backendProfile, isInitialized]);

  // Track changes to determine dirty state
  useEffect(() => {
    const hasChanges = JSON.stringify(profile) !== JSON.stringify(originalProfile);
    setIsDirty(hasChanges);
  }, [profile, originalProfile]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      if (currentTier === "demo") {
        setToast({
          message: "Feature preview in demo mode",
          type: "info",
          isVisible: true
        });
        return;
      }

      // Transform UI data to backend format and save
      const backendData = transformUIToBackend(profile);
      await updateUserProfileAction(backendData);

      setOriginalProfile(profile);
      setIsDirty(false);
      
      setToast({
        message: "✅ Preferences saved successfully!",
        type: "success",
        isVisible: true
      });
      
      console.log("Profile saved:", backendData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save profile";
      setSaveError(errorMessage);
      setToast({
        message: errorMessage,
        type: "error",
        isVisible: true
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setProfile(originalProfile);
    setIsDirty(false);
    setSaveError(null);
  };

  const updateProfile = (updates: Partial<UserProfile>) => {
    setProfile(prev => ({ ...prev, ...updates }));
  };

  const isTabLocked = (tab: string) => {
    // Goals: allow limited access on Starter (not locked)
    if (tab === "goals") return false;
    // Household: lock for non-core_plus users
    if (tab === "household" && currentTier !== "core_plus") return true;
    return false;
  };

  // Show loading state while profile data is being fetched
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-lynx flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-warning mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your kitchen profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profile data failed to load
  if (profileError) {
    return (
      <div className="min-h-screen bg-lynx flex items-center justify-center">
        <div className="text-center max-w-md">
          <ChefLexMessage
            type="GENERATION_FAILED"
            onPrimaryAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lynx">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-gradient-to-br from-warning to-coral text-white">
                  {userName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-xl font-medium">Kitchen Profile</h1>
                  <Badge className={`text-xs ${getTierColor(currentTier)}`}>
                    {getTierDisplayName(currentTier)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Tell Chef Lex how you like to cook—I'll handle the planning. 🍳
                </p>
                <p className="text-xs text-muted-foreground mt-1">{userEmail}</p>
              </div>
            </div>
            
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        
        {/* Save Bar */}
        {isDirty && (
          <SaveBar
            onSave={handleSave}
            onDiscard={handleDiscard}
            isSaving={isSaving}
          />
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Profile Form */}
          <div className="lg:col-span-3">
            {saveError && (
              <div className="mb-6">
                <ChefLexMessage
                  type="GENERATION_FAILED"
                  onPrimaryAction={() => setSaveError(null)}
                />
              </div>
            )}

            {/* Household Size Upgrade Prompt */}
            {onUpgrade && (
              <div className="mb-6">
                <HouseholdSizeUpgradePrompt
                  householdSize={profile.householdSize}
                  currentTier={currentTier}
                  onUpgrade={onUpgrade}
                />
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <div className="border-b bg-muted/30">
                    <TabsList className="w-full h-auto p-0 bg-transparent overflow-x-auto">
                      <div className="grid grid-cols-2 md:grid-cols-5 w-full gap-1">
                        <TabsTrigger value="dietary" className="data-[state=active]:bg-white whitespace-normal break-words text-xs md:text-sm leading-tight px-2 py-3 text-center">
                          Dietary & Restrictions
                        </TabsTrigger>
                        <TabsTrigger 
                          value="goals" 
                          className="data-[state=active]:bg-white relative whitespace-normal break-words text-xs md:text-sm leading-tight px-2 py-3 text-center"
                          disabled={isTabLocked("goals")}
                        >
                          Goals & Macros
                          {isTabLocked("goals") && (
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-coral rounded-full" />
                          )}
                        </TabsTrigger>
                        <TabsTrigger 
                          value="household" 
                          className="data-[state=active]:bg-white relative whitespace-normal break-words text-xs md:text-sm leading-tight px-2 py-3 text-center"
                          disabled={isTabLocked("household")}
                        >
                          Household & Equipment
                          {isTabLocked("household") && (
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-coral rounded-full" />
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="preferences" className="data-[state=active]:bg-white whitespace-normal break-words text-xs md:text-sm leading-tight px-2 py-3 text-center">
                          Preferences & Pantry
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="data-[state=active]:bg-white whitespace-normal break-words text-xs md:text-sm leading-tight px-2 py-3 text-center">
                          Notifications & Email
                        </TabsTrigger>
                      </div>
                    </TabsList>
                  </div>

                  <div className="p-6">
                    <TabsContent value="dietary" className="mt-0">
                      <DietarySection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                      />
                    </TabsContent>

                    <TabsContent value="goals" className="mt-0">
                      <GoalsMacrosSection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                        isLocked={isTabLocked("goals")}
                      />
                    </TabsContent>

                    <TabsContent value="household" className="mt-0">
                      <HouseholdEquipmentSection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                        isLocked={isTabLocked("household")}
                      />
                    </TabsContent>

                    <TabsContent value="preferences" className="mt-0">
                      <PreferencesPantrySection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                      />
                    </TabsContent>

                    <TabsContent value="notifications" className="mt-0">
                      <NotificationsEmailSection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                      />
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Rail */}
          <div className="space-y-4">
            {/* Profile Completeness */}
            <ProfileCompleteness
              profile={profile}
              currentTier={currentTier}
            />
            
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={() => {
                    if (isDirty) {
                      setToast({
                        message: "Save changes first to regenerate with new profile",
                        type: "info",
                        isVisible: true
                      });
                      return;
                    }
                    if (onUpgrade) {
                      onUpgrade();
                      setToast({
                        message: "🍳 Cooking up a fresh plan with your new profile!",
                        type: "success",
                        isVisible: true
                      });
                    }
                  }}
                  className="w-full justify-start whitespace-normal break-words text-left"
                  variant="outline"
                  disabled={isDirty}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Re-run plan with new profile
                </Button>
                <Button
                  onClick={() => {
                    if (isDirty) {
                      setToast({
                        message: "Save changes first to preview email",
                        type: "info",
                        isVisible: true
                      });
                      return;
                    }
                    if (onUpgrade) {
                      onUpgrade();
                      setToast({
                        message: "🍳 Cooking up a fresh plan with your new profile!",
                        type: "success",
                        isVisible: true
                      });
                    }
                  }}
                  className="w-full justify-start"
                  variant="outline"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Preview weekly email
                </Button>
                {isDirty && (
                  <p className="text-xs text-muted-foreground">
                    Save changes to regenerate with new profile
                  </p>
                )}
              </CardContent>
            </Card>

            {currentTier === "demo" && (
              <Card className="border-coral/20 bg-coral/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-coral" />
                    <span className="text-sm font-medium text-dusk">Demo Mode</span>
                  </div>
                  <p className="text-xs text-supporting mb-3">
                    Changes won't be saved in demo mode. Start your free trial to unlock full profile customization.
                  </p>
                  <Button 
                    size="sm" 
                    className="w-full bg-coral hover:bg-coral/90 text-ink"
                    onClick={onUpgrade}
                  >
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ProfileToast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
}
